<script setup lang="ts">
import { ref, watch } from 'vue'
import { Button } from '@/components/ui/button'
import { ClipboardList, Plus, Trash2, RefreshCw, Box, Link2, FileCheck2 } from 'lucide-vue-next'
import BaseVisualEditor from './BaseVisualEditor.vue'
import MermaidCodeEditor from './MermaidCodeEditor.vue'
import MermaidLivePreview from './MermaidLivePreview.vue'

const props = defineProps<{
  open: boolean
}>()

const emit = defineEmits<{
  'update:open': [value: boolean]
  'save': [code: string]
}>()

const showCode = ref(true)
const copied = ref(false)
const editableCode = ref('')

// Interfaces
interface Requirement {
  name: string
  type: string
  id: string
  text: string
  risk: string
  verifyMethod: string
}

interface ReqElement {
  name: string
  type: string
  docRef: string
}

interface ReqRelation {
  source: string
  type: string
  target: string
}

// Estados visuales
const requirements = ref<Requirement[]>([
  {
    name: 'Req_Autenticacion',
    type: 'requirement',
    id: 'REQAUTH01',
    text: 'El sistema debe permitir el inicio de sesión.',
    risk: 'high',
    verifyMethod: 'test'
  }
])

const elements = ref<ReqElement[]>([
  {
    name: 'Modulo_Login',
    type: 'software_component',
    docRef: 'doc_123'
  }
])

const relations = ref<ReqRelation[]>([
  {
    source: 'Modulo_Login',
    type: 'satisfies',
    target: 'Req_Autenticacion'
  }
])

// Catálogos
const reqTypes = [
  'requirement', 'functionalRequirement', 'interfaceRequirement', 
  'performanceRequirement', 'physicalRequirement', 'designConstraint'
]
const riskTypes = ['low', 'medium', 'high']
const verifyTypes = ['analysis', 'demonstration', 'inspection', 'test']
const relationTypes = ['contains', 'copies', 'derives', 'satisfies', 'verifies', 'refines', 'traces']

// Acciones Requerimientos
const addRequirement = () => {
  requirements.value.push({
    name: `Req_${requirements.value.length + 1}`,
    type: 'requirement',
    id: `REQ-${requirements.value.length + 1}`,
    text: 'Descripción del requisito.',
    risk: 'medium',
    verifyMethod: 'inspection'
  })
}
const removeRequirement = (idx: number) => {
  requirements.value.splice(idx, 1)
}

// Acciones Elementos
const addElement = () => {
  elements.value.push({
    name: `Elemento_${elements.value.length + 1}`,
    type: 'component',
    docRef: ''
  })
}
const removeElement = (idx: number) => {
  elements.value.splice(idx, 1)
}

// Acciones Relaciones
const addRelation = () => {
  const allNames = [...requirements.value.map(r => r.name), ...elements.value.map(e => e.name)]
  const source = allNames.length > 0 ? allNames[allNames.length - 1] : ''
  const target = allNames.length > 1 ? allNames[0] : source
  relations.value.push({
    source,
    type: 'satisfies',
    target
  })
}
const removeRelation = (idx: number) => {
  relations.value.splice(idx, 1)
}

// Utils
const formatName = (name: string) => name.trim().replace(/\s+/g, '_')

// Generar Mermaid
const generateMermaid = () => {
  let code = `requirementDiagram\n\n`

  requirements.value.forEach(req => {
    const safeName = formatName(req.name)
    if (!safeName) return
    const safeId = req.id.replace(/[^a-zA-Z0-9]/g, '') || 'ID1'
    code += `  ${req.type} ${safeName} {\n`
    code += `    id: ${safeId}\n`
    code += `    text: ${req.text || 'Sin texto'}\n`
    code += `    risk: ${req.risk}\n`
    code += `    verifymethod: ${req.verifyMethod}\n`
    code += `  }\n\n`
  })

  elements.value.forEach(el => {
    const safeName = formatName(el.name)
    if (!safeName) return
    code += `  element ${safeName} {\n`
    code += `    type: ${el.type || 'unknown'}\n`
    if (el.docRef.trim()) {
      code += `    docRef: ${el.docRef.trim()}\n`
    }
    code += `  }\n\n`
  })

  relations.value.forEach(rel => {
    const src = formatName(rel.source)
    const tgt = formatName(rel.target)
    if (src && tgt) {
      code += `  ${src} - ${rel.type} -> ${tgt}\n`
    }
  })
  
  editableCode.value = code
}

// Sincronizar desde Código a Visual
const syncToVisual = () => {
  const code = editableCode.value
  const lines = code.split('\n')
  
  const newReqs: Requirement[] = []
  const newElems: ReqElement[] = []
  const newRels: ReqRelation[] = []
  
  const reqStartRegex = /^(requirement|functionalRequirement|interfaceRequirement|performanceRequirement|physicalRequirement|designConstraint)\s+([^{]+)\s*{/
  const elemStartRegex = /^element\s+([^{]+)\s*{/
  const relationRegex = /^([a-zA-Z0-9_]+)\s*-\s*([a-zA-Z0-9_]+)\s*->\s*([a-zA-Z0-9_]+)/
  
  let currentBlock: 'req' | 'elem' | null = null
  let currentObj: any = null

  for (const line of lines) {
    const tLine = line.trim()
    if (!tLine || tLine === 'requirementDiagram') continue

    // Detect block end
    if (tLine === '}') {
      if (currentBlock === 'req' && currentObj) {
        newReqs.push(currentObj as Requirement)
      } else if (currentBlock === 'elem' && currentObj) {
        newElems.push(currentObj as ReqElement)
      }
      currentBlock = null
      currentObj = null
      continue
    }

    // Process block contents
    if (currentBlock === 'req') {
      if (tLine.startsWith('id:')) currentObj.id = tLine.replace('id:', '').trim()
      else if (tLine.startsWith('text:')) currentObj.text = tLine.replace('text:', '').trim()
      else if (tLine.startsWith('risk:')) currentObj.risk = tLine.replace('risk:', '').trim()
      else if (tLine.startsWith('verifymethod:')) currentObj.verifyMethod = tLine.replace('verifymethod:', '').trim()
      continue
    }

    if (currentBlock === 'elem') {
      if (tLine.startsWith('type:')) currentObj.type = tLine.replace('type:', '').trim()
      else if (tLine.startsWith('docref:')) currentObj.docRef = tLine.replace('docref:', '').trim()
      continue
    }

    // Detect new blocks or relations
    if (reqStartRegex.test(tLine)) {
      const match = tLine.match(reqStartRegex)!
      currentBlock = 'req'
      currentObj = {
        type: match[1],
        name: match[2].trim(),
        id: '', text: '', risk: 'medium', verifyMethod: 'test'
      }
    } else if (elemStartRegex.test(tLine)) {
      const match = tLine.match(elemStartRegex)!
      currentBlock = 'elem'
      currentObj = {
        name: match[1].trim(),
        type: 'component',
        docRef: ''
      }
    } else if (relationRegex.test(tLine)) {
      const match = tLine.match(relationRegex)!
      newRels.push({
        source: match[1].trim(),
        type: match[2].trim(),
        target: match[3].trim()
      })
    }
  }

  if (newReqs.length > 0) requirements.value = newReqs
  if (newElems.length > 0) elements.value = newElems
  if (newRels.length > 0) relations.value = newRels
}

// Watchers
watch(() => props.open, (newVal) => {
  if (newVal) {
    const editingCode = localStorage.getItem('editingDiagramCode');
    if (editingCode) {
      setTimeout(() => {
        editableCode.value = editingCode;
        localStorage.removeItem('editingDiagramCode');
        syncToVisual();
      }, 300);
      return;
    }
  }

  if (newVal && !editableCode.value) {
    generateMermaid()
  }
}, { immediate: true })

watch([requirements, elements, relations], () => {
  generateMermaid()
}, { deep: true })

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(editableCode.value)
    copied.value = true
    setTimeout(() => copied.value = false, 2000)
  } catch (err) {
    console.error('Failed to copy text: ', err)
  }
}

const saveAndClose = () => {
  emit('save', editableCode.value)
  emit('update:open', false)
}
</script>

<template>
  <BaseVisualEditor
    :open="open"
    @update:open="$emit('update:open', $event)"
    title="Diagrama de Requisitos"
    description="Diseña la trazabilidad entre requisitos del sistema y elementos que los satisfacen (Estilo SysML)."
    :showCode="showCode"
    @update:showCode="showCode = $event"
    :copied="copied"
    @copy="copyToClipboard"
    @save="saveAndClose"
  >
    <template #icon>
      <ClipboardList class="w-5 h-5 text-primary" />
    </template>

    <template #toolbar>
      <Button size="sm" variant="default" @click="addRequirement"><FileCheck2 class="w-4 h-4 mr-2" /> Añadir Requisito</Button>
      <Button size="sm" variant="secondary" @click="addElement"><Box class="w-4 h-4 mr-2" /> Añadir Elemento</Button>
      <Button size="sm" variant="outline" @click="addRelation"><Link2 class="w-4 h-4 mr-2" /> Vincular (Relación)</Button>
    </template>

    <template #canvas>
      <div class="h-full w-full overflow-y-auto p-6 bg-slate-50 dark:bg-slate-900/50 flex flex-col gap-6">
        
        <!-- Requisitos -->
        <div class="bg-card border rounded-lg p-4 shadow-sm flex flex-col gap-4">
          <h3 class="font-semibold text-lg border-b pb-2 flex items-center gap-2"><FileCheck2 class="w-5 h-5"/> Requisitos</h3>
          
          <div v-if="requirements.length === 0" class="text-center text-muted-foreground py-4 text-xs border border-dashed rounded">
            Sin requisitos definidos.
          </div>

          <div class="grid grid-cols-1 2xl:grid-cols-2 gap-4">
            <div 
              v-for="(req, idx) in requirements" 
              :key="'req'+idx"
              class="flex flex-col gap-2 bg-muted/40 border p-3 rounded-md relative"
            >
              <button 
                @click="removeRequirement(idx)" 
                class="absolute right-2 top-2 text-muted-foreground hover:text-destructive transition-colors bg-background p-1 rounded-md"
              >
                <Trash2 class="w-4 h-4" />
              </button>

              <div class="grid grid-cols-2 gap-2 pr-8">
                <div class="flex flex-col gap-1">
                  <label class="text-[10px] font-bold uppercase text-muted-foreground ml-1">Nombre (Alias)</label>
                  <input v-model="req.name" class="bg-background border rounded px-2 py-1 text-sm focus:ring-1 focus:outline-none" placeholder="Nombre" />
                </div>
                <div class="flex flex-col gap-1">
                  <label class="text-[10px] font-bold uppercase text-muted-foreground ml-1">Tipo de Requisito</label>
                  <select v-model="req.type" class="bg-background border rounded px-2 py-1 text-sm focus:ring-1 focus:outline-none">
                    <option v-for="t in reqTypes" :key="t" :value="t">{{ t }}</option>
                  </select>
                </div>
              </div>

              <div class="grid grid-cols-3 gap-2">
                <div class="flex flex-col gap-1">
                  <label class="text-[10px] font-bold uppercase text-muted-foreground ml-1">ID Ref</label>
                  <input v-model="req.id" class="bg-background border rounded px-2 py-1 text-sm focus:ring-1 focus:outline-none" placeholder="REQ-01" />
                </div>
                <div class="flex flex-col gap-1">
                  <label class="text-[10px] font-bold uppercase text-muted-foreground ml-1">Riesgo</label>
                  <select v-model="req.risk" class="bg-background border rounded px-2 py-1 text-sm focus:ring-1 focus:outline-none">
                    <option v-for="r in riskTypes" :key="r" :value="r">{{ r }}</option>
                  </select>
                </div>
                <div class="flex flex-col gap-1">
                  <label class="text-[10px] font-bold uppercase text-muted-foreground ml-1">Verificación</label>
                  <select v-model="req.verifyMethod" class="bg-background border rounded px-2 py-1 text-sm focus:ring-1 focus:outline-none">
                    <option v-for="v in verifyTypes" :key="v" :value="v">{{ v }}</option>
                  </select>
                </div>
              </div>

              <div class="flex flex-col gap-1 mt-1">
                <label class="text-[10px] font-bold uppercase text-muted-foreground ml-1">Texto (Descripción)</label>
                <textarea v-model="req.text" class="bg-background border rounded px-2 py-1 text-sm focus:ring-1 focus:outline-none h-16 resize-none" placeholder="Describe el requerimiento..."></textarea>
              </div>
            </div>
          </div>
        </div>

        <!-- Elementos -->
        <div class="bg-card border rounded-lg p-4 shadow-sm flex flex-col gap-4">
          <h3 class="font-semibold text-lg border-b pb-2 flex items-center gap-2"><Box class="w-5 h-5"/> Elementos del Sistema</h3>
          
          <div v-if="elements.length === 0" class="text-center text-muted-foreground py-4 text-xs border border-dashed rounded">
            Sin elementos definidos.
          </div>

          <div class="grid grid-cols-1 2xl:grid-cols-2 gap-4">
            <div 
              v-for="(el, idx) in elements" 
              :key="'el'+idx"
              class="flex flex-col gap-2 bg-muted/40 border p-3 rounded-md relative"
            >
              <button 
                @click="removeElement(idx)" 
                class="absolute right-2 top-2 text-muted-foreground hover:text-destructive transition-colors bg-background p-1 rounded-md"
              >
                <Trash2 class="w-4 h-4" />
              </button>

              <div class="grid grid-cols-2 gap-2 pr-8">
                <div class="flex flex-col gap-1">
                  <label class="text-[10px] font-bold uppercase text-muted-foreground ml-1">Nombre (Alias)</label>
                  <input v-model="el.name" class="bg-background border rounded px-2 py-1 text-sm focus:ring-1 focus:outline-none" placeholder="Nombre" />
                </div>
                <div class="flex flex-col gap-1">
                  <label class="text-[10px] font-bold uppercase text-muted-foreground ml-1">Tipo Técnico</label>
                  <input v-model="el.type" class="bg-background border rounded px-2 py-1 text-sm focus:ring-1 focus:outline-none" placeholder="Ej. software_component" />
                </div>
              </div>

              <div class="flex flex-col gap-1 mt-1">
                <label class="text-[10px] font-bold uppercase text-muted-foreground ml-1">Ref Documento (Opcional)</label>
                <input v-model="el.docRef" class="bg-background border rounded px-2 py-1 text-sm focus:ring-1 focus:outline-none" placeholder="Ej. doc_arq_12" />
              </div>
            </div>
          </div>
        </div>

        <!-- Relaciones -->
        <div class="bg-card border rounded-lg p-4 shadow-sm flex flex-col gap-4">
          <h3 class="font-semibold text-lg border-b pb-2 flex items-center gap-2"><Link2 class="w-5 h-5"/> Trazabilidad (Relaciones)</h3>
          
          <div v-if="relations.length === 0" class="text-center text-muted-foreground py-4 text-xs border border-dashed rounded">
            Sin relaciones de trazabilidad.
          </div>

          <div class="flex flex-col gap-2">
            <div 
              v-for="(rel, idx) in relations" 
              :key="'rel'+idx"
              class="flex flex-wrap items-center gap-2 bg-background border p-2 rounded-md shadow-sm"
            >
              <select v-model="rel.source" class="bg-muted border rounded px-2 py-1 text-sm focus:outline-none flex-1 min-w-[120px]">
                <optgroup label="Elementos">
                  <option v-for="e in elements" :key="'src_e_'+e.name" :value="e.name">{{ e.name }}</option>
                </optgroup>
                <optgroup label="Requisitos">
                  <option v-for="r in requirements" :key="'src_r_'+r.name" :value="r.name">{{ r.name }}</option>
                </optgroup>
              </select>

              <select v-model="rel.type" class="bg-primary/10 border-primary/20 text-primary border rounded px-2 py-1 text-sm focus:outline-none font-semibold">
                <option v-for="t in relationTypes" :key="t" :value="t">- {{ t }} -></option>
              </select>

              <select v-model="rel.target" class="bg-muted border rounded px-2 py-1 text-sm focus:outline-none flex-1 min-w-[120px]">
                <optgroup label="Requisitos">
                  <option v-for="r in requirements" :key="'tgt_r_'+r.name" :value="r.name">{{ r.name }}</option>
                </optgroup>
                <optgroup label="Elementos">
                  <option v-for="e in elements" :key="'tgt_e_'+e.name" :value="e.name">{{ e.name }}</option>
                </optgroup>
              </select>

              <button 
                @click="removeRelation(idx)" 
                class="text-muted-foreground hover:bg-destructive hover:text-white p-2 rounded-full transition-colors ml-2 shrink-0"
              >
                <Trash2 class="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

      </div>
    </template>

    <template #code-panel>
      <div class="text-sm font-bold p-2 bg-muted-foreground/10 text-muted-foreground flex items-center justify-between border-b shrink-0">
        Mermaid (Sincronizado)
        <Button size="sm" variant="default" class="h-7 text-xs" @click="syncToVisual">
          <RefreshCw class="w-3 h-3 mr-2" />
          Sincronizar a Visual
        </Button>
      </div>
      <MermaidCodeEditor v-model="editableCode" class="flex-1 min-h-0" />
    </template>

    <template #live-preview>
      <MermaidLivePreview :code="editableCode" />
    </template>
  </BaseVisualEditor>
</template>
